import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import { fetchBackofficeGlobalSearch } from './api';
import type { GlobalSearchEntityType, GlobalSearchGroup } from './types';
const paths: Record<GlobalSearchEntityType, string> = {
  study: '/projects',
  organization: '/organizations',
  user: '/users',
  voucher: '/vouchers',
  interview: '/interviews',
};
const entityTypes: GlobalSearchEntityType[] = [
  'study',
  'organization',
  'user',
  'voucher',
  'interview',
];

function selectedTypesFromParams(value: string | null): GlobalSearchEntityType[] {
  const selected = new Set(value?.split(',') ?? []);
  return entityTypes.filter((entityType) => selected.has(entityType));
}

export function BackofficeSearchResultsPage() {
  useBackofficePageTitle('Search');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get('q')?.trim() ?? '';
  const selectedEntityTypes = selectedTypesFromParams(params.get('entityTypes'));
  const selectedEntityTypesKey = selectedEntityTypes.join(',');
  const [groups, setGroups] = useState<GlobalSearchGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!q) {
      setGroups([]);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const query = new URLSearchParams({ q, limit: '20', offset: '0' });
    if (selectedEntityTypes.length > 0) query.set('entityTypes', selectedEntityTypesKey);
    fetchBackofficeGlobalSearch(query, controller.signal)
      .then((response) => {
        const item = response.groups.flatMap((group) => group.items);
        if (response.matchCount === 1 && item.length === 1) {
          navigate(`${paths[item[0].entityType]}/${item[0].id}`, { replace: true });
          return;
        }
        setGroups(response.groups);
      })
      .catch((caught) => {
        if (!controller.signal.aborted)
          setError(caught instanceof Error ? caught.message : 'Failed to search');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [navigate, q, selectedEntityTypesKey]);
  async function loadMore(entityType: Exclude<GlobalSearchEntityType, 'interview'>) {
    const current = groups.find((group) => group.entityType === entityType);
    if (!current) return;
    const response = await fetchBackofficeGlobalSearch(
      new URLSearchParams({
        q,
        entityTypes: entityType,
        limit: '20',
        offset: String(current.items.length),
      })
    );
    const next = response.groups[0];
    if (next)
      setGroups((existing) =>
        existing.map((group) =>
          group.entityType === entityType
            ? { ...next, items: [...group.items, ...next.items], page: { ...next.page, offset: 0 } }
            : group
        )
      );
  }
  return (
    <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        <PageHeader showShareButton={false}>Search</PageHeader>
        {q ? (
          <p className="text-neutral-600">Results for “{q}”</p>
        ) : (
          <p className="text-neutral-600">Enter a search term above.</p>
        )}
        {loading ? <p>Searching…</p> : null}
        {error ? <p className="text-red-700">{error}</p> : null}
        {!loading && q && !error && groups.length === 0 ? (
          <p>No results found.</p>
        ) : (
          groups.map((group) => (
            <section key={group.entityType} className="rounded-md border border-neutral-200 p-4">
              <h2 className="font-semibold capitalize">
                {group.entityType}s ({group.page.total})
              </h2>
              <ul className="mt-3 space-y-2">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <Link
                      className="focus-ring block rounded p-2 hover:bg-backoffice-50"
                      to={`${paths[item.entityType]}/${item.id}`}
                    >
                      <span className="font-medium">{item.title}</span>
                      {item.subtitle ? (
                        <span className="ml-2 text-sm text-neutral-600">{item.subtitle}</span>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
              {group.items.length < group.page.total && group.entityType !== 'interview' ? (
                <button
                  type="button"
                  className="focus-ring mt-3 rounded border border-neutral-300 px-3 py-1 text-sm"
                  onClick={() =>
                    void loadMore(group.entityType as Exclude<GlobalSearchEntityType, 'interview'>)
                  }
                >
                  Load more
                </button>
              ) : null}
            </section>
          ))
        )}
      </div>
    </main>
  );
}
